import React, { useState, useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  LEGAL_ENTITY_TYPES,
  getLegalEntityType,
  normalizeRegistrationNumber,
  registrationLabelFor,
  registrationPlaceholderFor,
  validateRegistrationNumber
} from '../../../utils/validation/legalEntityType';
import {
  SOUTH_AFRICAN_TAX_NUMBER_ERROR,
  SOUTH_AFRICAN_VAT_NUMBER_ERROR,
  normalizeTaxOrVatNumber,
  validateSouthAfricanTaxNumber,
  validateSouthAfricanVatNumber
} from '../../../utils/validation/taxVatNumber';
import {
  normalizeTaxPinOrIdNumber,
  validateSouthAfricanIdNumber,
} from '../../../utils/validation/taxPinAndSouthAfricanId';
import { SOUTH_AFRICAN_TAX_PIN_ERROR, isValidSarsTaxPin } from '../../../utils/validation/taxPinAndSouthAfricanId';
import { validatePassportNumber } from '../../../utils/validation/passportNumber';
import { toLocalDateString } from '../../../utils/localDate';
import {
  RegistrationNumberType,
  RepresentativeCapacity,
} from '../../../services/schemas/loanServiceSchemas';

type DirectorAddress = {
  addressType: 'RESIDENTIAL' | 'POSTAL' | 'BUSINESS';
  streetAddress: string;
  suburb?: string;
  city: string;
  stateProvince?: string;
  postalCode?: string;
  country: string;
  isPrimary?: boolean;
};

type Director = {
  firstName: string;
  lastName: string;
  idType?: 'SA_ID' | 'PASSPORT';
  idNumber: string;
  nationality?: 'SOUTH_AFRICA' | 'ZIMBABWE' | 'NAMIBIA' | 'UNITED_KINGDOM' | 'OTHER';
  dateOfBirth?: string;
  designation?: string;
  ownershipPercentage?: number;
  isExecutive?: boolean;
  appointmentDate?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER' | 'PREFER_NOT_TO_SAY';
  isDisabled?: boolean;
  isPreviouslyDisadvantaged?: boolean;
  race?: 'BLACK' | 'COLOURED' | 'WHITE' | 'ASIAN' | 'OTHER' | 'PREFER_NOT_TO_SAY';
  email?: string;
  cellNumber?: string;
  addresses?: DirectorAddress[];
}

type CompanyForm = {
  company: {
    companyName: string;
    companyRegNumber: string;
    /** Decides which registration number format is valid. See utils/validation/legalEntityType. */
    legalEntityType: string;
    /** Optional: how to read the number where the legal form alone does not say. */
    registrationNumberType?: string;
    representativeIdNumber?: string;
    representativeCapacity?: string;
    representativeMandate?: string;
    /** Which director (row index) is the contact person, when one is; '' or absent when someone else */
    contactDirectorIndex?: string;
    taxNumber: string;
    vatNumber?: string;
    contactPersonFirstName: string;
    contactPersonLastName: string;
    contactTel: string;
    contactEmail: string;
    address: {
      type: 'BUSINESS';
      lineOne: string;
      lineTwo?: string;
      suburb?: string;
      city: string;
      province: string;
      code: string;
    };
    representativeOccupation?: string;
    turnover: number;
    industrySector?: string;
    incomeType?: 'PO' | 'TENDER' | 'PRODUCTS_AND_SERVICES' | 'OTHER';
    incomeDescription?: string;
    industryCategory?: 'GOVERNMENT' | 'PRIVATE_SECTOR' | 'HOUSEHOLD' | 'PERSONAL' | 'OTHER';
    industryDescription?: string;
    taxPin?: string;
    directors: Director[];
  }
}

// Helper function to get date of birth from SA ID (digits 1-6)
const formatDOBFromID = (idNumber: string): string => {
  if (idNumber.length < 6) return '';

  const YY = idNumber.substring(0, 2);
  const MM = idNumber.substring(2, 4);
  const DD = idNumber.substring(4, 6);

  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100);
  const yearSuffix = currentYear % 100;
  const century = parseInt(YY, 10) > (yearSuffix + 20) ? `${currentCentury - 1}` : `${currentCentury}`;
  const YYYY = century + YY;

  try {
    const dateString = `${YYYY}-${MM}-${DD}`;
    const date = new Date(dateString);
    if (date.toISOString().substring(0, 10) === dateString) {
      return dateString;
    }
    return '';
  } catch (e) {
    return '';
  }
};

// Helper function to get gender from SA ID number (digits 7-10)
const formatGenderFromID = (idNumber: string): Director['gender'] | undefined => {
    if (idNumber.length < 10) return undefined;
    
    const genderCode = parseInt(idNumber.substring(6, 10), 10);

    if (isNaN(genderCode)) {
        return undefined;
    }

    // 0000-4999 is Female, 5000-9999 is Male
    if (genderCode >= 5000) {
        return 'MALE';
    } else {
        return 'FEMALE';
    }
};


/**
 * The number a business usually holds for each legal form, used to default the optional
 * registration-number type. It stays an override: an Inc law firm may be known by its practice
 * number instead of its CIPC number.
 */
const DEFAULT_REGISTRATION_NUMBER_TYPE: Record<string, RegistrationNumberType> = {
  PTY_LTD: RegistrationNumberType.CIPC,
  INCORPORATED: RegistrationNumberType.CIPC,
  PUBLIC_COMPANY: RegistrationNumberType.CIPC,
  CLOSE_CORPORATION: RegistrationNumberType.CIPC,
  NPC: RegistrationNumberType.CIPC,
  CO_OPERATIVE: RegistrationNumberType.CIPC,
  TRUST: RegistrationNumberType.TRUST_NUMBER,
  MEDICAL_PRACTICE: RegistrationNumberType.PRACTICE_NUMBER,
  SOLE_PROPRIETOR: RegistrationNumberType.ID_NUMBER,
  NPO: RegistrationNumberType.OTHER,
  PARTNERSHIP: RegistrationNumberType.OTHER,
  OTHER: RegistrationNumberType.OTHER,
};

export default function CompanyInfoStep() {
  const { register, formState: { errors }, setValue, getValues, control, trigger } = useFormContext<CompanyForm>();
  const companyErrors = errors.company || {};
  const todayDate = toLocalDateString();
  
  const watchedDirectors = useWatch({
    control,
    name: 'company.directors',
    defaultValue: [] as CompanyForm['company']['directors'],
  }) || [];
  
  // The director who is the contact person, if one is (finding #4, business test 22 Oct 2026)
  const contactDirectorIndex = useWatch({ control, name: 'company.contactDirectorIndex' }) || '';

  // The chosen legal form drives the registration-number label, placeholder and validation.
  const watchedLegalEntityType = useWatch({
    control,
    name: 'company.legalEntityType',
  });
  const selectedLegalEntityType = getLegalEntityType(watchedLegalEntityType);

  // Default the optional number type from the legal form; the user can still override it.
  // Only when the legal form is actually changed, or no type has been chosen yet: running on every
  // mount, draft resume or customer prefill overwrote the type the user had picked (finding #3).
  const previousLegalEntityType = useRef(watchedLegalEntityType);
  useEffect(() => {
    const previous = previousLegalEntityType.current;
    previousLegalEntityType.current = watchedLegalEntityType;
    if (!watchedLegalEntityType) return;
    const expected = DEFAULT_REGISTRATION_NUMBER_TYPE[watchedLegalEntityType];
    if (!expected) return;
    const chosen = getValues('company.registrationNumberType');
    const legalFormChanged = !!previous && previous !== watchedLegalEntityType;
    if (legalFormChanged || !chosen) {
      setValue('company.registrationNumberType', expected);
    }
  }, [watchedLegalEntityType, setValue, getValues]);
  
  const [directors, setDirectors] = useState(() => {
    const initialDirectors = getValues('company.directors') || [];
    return initialDirectors.length > 0 ? initialDirectors.map((_, index) => ({ id: index + 1 })) : [{ id: 1 }];
  });
  const [directorIdLiveErrors, setDirectorIdLiveErrors] = useState<Record<number, string>>({});
  // The contact person is often a director. Choosing which one (Director 1, 2, 3...) hides the contact
  // fields: they follow that director's details as they are captured below (finding #4).
  const contactDirector = contactDirectorIndex !== '' ? (watchedDirectors || [])[Number(contactDirectorIndex)] : undefined;
  const contactIsDirector = contactDirectorIndex !== '';
  const directorName = (d?: Director) => `${d?.firstName || ''} ${d?.lastName || ''}`.trim();
  const chooseContactDirector = (key: string) => {
    const wasDirector = contactIsDirector;
    setValue('company.contactDirectorIndex', key);
    if (key === '' && wasDirector) {
      // Someone else now: start their details afresh rather than keep the director's
      (['company.contactPersonFirstName', 'company.contactPersonLastName', 'company.contactTel', 'company.contactEmail',
        'company.representativeIdNumber', 'company.representativeCapacity'] as const).forEach(f => setValue(f, ''));
    }
  };
  useEffect(() => {
    if (!contactIsDirector) return;
    const d = contactDirector || ({} as Director);
    const sync = (field: Parameters<typeof setValue>[0], value?: string) => {
      if ((getValues(field) || '') !== (value || '')) setValue(field, (value || '') as any, { shouldValidate: false });
    };
    sync('company.contactPersonFirstName', d.firstName);
    sync('company.contactPersonLastName', d.lastName);
    sync('company.representativeIdNumber', d.idNumber);
    sync('company.contactTel', d.cellNumber);
    sync('company.contactEmail', d.email);
    sync('company.representativeCapacity', 'DIRECTOR');
  }, [contactIsDirector, contactDirector?.firstName, contactDirector?.lastName, contactDirector?.idNumber,
      contactDirector?.cellNumber, contactDirector?.email, setValue, getValues]);
  // An application or customer reopened with a director as representative: select that director
  useEffect(() => {
    if (getValues('company.contactDirectorIndex') || getValues('company.representativeCapacity') !== 'DIRECTOR') return;
    const id = (getValues('company.representativeIdNumber') || '').trim();
    const match = (watchedDirectors || []).findIndex(d => !!id && (d?.idNumber || '').trim() === id);
    if (match >= 0) setValue('company.contactDirectorIndex', String(match));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedDirectors.length]);

   // Initialize directors from form values
  useEffect(() => {
    if (watchedDirectors.length > 0 && directors.length !== watchedDirectors.length) {
      setDirectors(watchedDirectors.map((_, index) => ({ id: index + 1 })));
    }
  }, [watchedDirectors.length, directors.length]);

  // Ensure ID type defaults to SA_ID for each director row.
  useEffect(() => {
    watchedDirectors.forEach((director, index) => {
      if (!director?.idType) {
        setValue(`company.directors.${index}.idType` as const, 'SA_ID', { shouldValidate: false });
      }
    });
  }, [watchedDirectors, setValue]);

  const defaultNewDirector: Director = {
    firstName: '',
    lastName: '',
    idType: 'SA_ID',
    idNumber: '',
    nationality: 'SOUTH_AFRICA',
    dateOfBirth: '',
    designation: '',
    ownershipPercentage: 0,
    isExecutive: false,
    appointmentDate: '',
    gender: undefined,
    isDisabled: false,
    isPreviouslyDisadvantaged: false,
    race: undefined,
    addresses: []
  };
  
  useEffect(() => {
    setValue('company.address.type', 'BUSINESS');
  
    // Update form values
    const currentDirectors = getValues('company.directors') || [];
    if (currentDirectors.length === 0) {
      setValue('company.directors', [defaultNewDirector]);
    }
  }, [setValue, getValues]);

  useEffect(() => {
    watchedDirectors.forEach((director, index) => {
      const { idType, idNumber, dateOfBirth: directorDob, gender: directorGender } = director;
      const idValidationResult = validateSouthAfricanIdNumber(idNumber);
      const isSAIDValid = idValidationResult === true;

      // Only auto-populate from ID if it's an SA ID and the field is empty
      // Never overwrite values that came from the API
      if (idType === 'SA_ID' && isSAIDValid) {
        // 1. Populate Date of Birth (DOB) - only if not already set
        if (!directorDob) {
          const dobFromID = formatDOBFromID(idNumber);
          if (dobFromID) {
            setValue(`company.directors.${index}.dateOfBirth` as const, dobFromID, { shouldValidate: false });
          }
        }

        // 2. Populate Gender - only if not already set
        if (!directorGender) {
          const genderFromID = formatGenderFromID(idNumber);
          if (genderFromID) {
            setValue(`company.directors.${index}.gender` as const, genderFromID, { shouldValidate: false });
          }
        }
      }
      // Note: We preserve DOB/gender values from API for all ID types
    });
  }, [watchedDirectors, setValue, getValues]);

  // Re-validate director ID whenever ID type changes so error state stays in sync.
  useEffect(() => {
    watchedDirectors.forEach((_, index) => {
      void trigger(`company.directors.${index}.idNumber` as const);
    });
  }, [watchedDirectors.map((d) => d?.idType).join('|'), trigger]);

  const addDirector = () => {
    const newDirectors = [...directors, { id: Date.now() }];
    setDirectors(newDirectors);

    const currentDirectors = getValues('company.directors') || [];
    setValue('company.directors', [
      ...currentDirectors,
      defaultNewDirector
    ]);
  };

  const removeDirector = (index: number) => {
    if (directors.length > 1) {
      const newDirectors = [...directors];
      newDirectors.splice(index, 1);
      setDirectors(newDirectors);
      setDirectorIdLiveErrors((prev) => {
        const next: Record<number, string> = {};
        Object.entries(prev).forEach(([k, v]) => {
          const key = Number(k);
          if (key < index) next[key] = v;
          if (key > index) next[key - 1] = v;
        });
        return next;
      });

      // Update form values
      const currentDirectors = getValues('company.directors') || [];
      const updatedDirectors = [...currentDirectors];
      updatedDirectors.splice(index, 1);
      setValue('company.directors', updatedDirectors);
    }
  };

  const updateDirectorIdLiveError = (
    index: number,
    idType: 'SA_ID' | 'PASSPORT' | undefined,
    value: string | undefined,
    onBlur: boolean = false
  ) => {
    const trimmed = normalizeTaxPinOrIdNumber(value);

    if (!trimmed) {
      setDirectorIdLiveErrors((prev) => ({
        ...prev,
        [index]: onBlur ? 'ID number is required' : ''
      }));
      return;
    }

    if (idType === 'SA_ID') {
      const validation = validateSouthAfricanIdNumber(trimmed);
      setDirectorIdLiveErrors((prev) => ({
        ...prev,
        [index]: validation === true ? '' : validation
      }));
      return;
    }

    if (idType === 'PASSPORT') {
      const validation = validatePassportNumber(trimmed);
      setDirectorIdLiveErrors((prev) => ({
        ...prev,
        [index]: validation === true ? '' : validation
      }));
      return;
    }

    setDirectorIdLiveErrors((prev) => ({
      ...prev,
      [index]: ''
    }));
  };

  return (
    <div className="form-section">
      <h6 className="form-section-title">Company Information</h6>
      <p className="text-muted mb-4">
        Enter the client company's details. All fields marked with * are required for processing.
      </p>

      <div className="row g-3">
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Company Details</h6>
            <div className="row g-3">
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="companyName">Company Name *</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.companyName ? 'is-invalid' : ''}`}
                    id="companyName"
                    placeholder="Enter client company name"
                    {...register('company.companyName', {
                      required: 'Company name is required'
                    })}
                  />
                  {companyErrors.companyName && (
                    <div className="invalid-feedback">{companyErrors.companyName.message as string}</div>
                  )}
                </div>
              </div>

              {/*
                Legal entity type must come BEFORE the registration number: it decides what a
                valid number is, and the field below relabels and revalidates from it. Asking
                for the number first is what made staff type a CIPC-shaped value for a trust
                just to get past the form.
              */}
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="legalEntityType">Legal Entity Type *</label>
                  <select
                    className={`form-select ${companyErrors.legalEntityType ? 'is-invalid' : ''}`}
                    id="legalEntityType"
                    {...register('company.legalEntityType', {
                      required: 'Select the legal entity type',
                      onChange: async () => {
                        // Revalidate the number against the newly chosen form, so switching
                        // from (Pty) Ltd to Trust clears a now-irrelevant CIPC error.
                        await trigger('company.companyRegNumber');
                      }
                    })}
                  >
                    <option value="">Select entity type...</option>
                    {LEGAL_ENTITY_TYPES.map((entityType) => (
                      <option key={entityType.code} value={entityType.code}>
                        {entityType.label}
                      </option>
                    ))}
                  </select>
                  {companyErrors.legalEntityType ? (
                    <div className="invalid-feedback">
                      {companyErrors.legalEntityType.message as string}
                    </div>
                  ) : (
                    selectedLegalEntityType?.hint && (
                      <small className="form-text text-muted">{selectedLegalEntityType.hint}</small>
                    )
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="companyRegNumber">
                    {registrationLabelFor(watchedLegalEntityType)} *
                  </label>
                  <Controller
                    name="company.companyRegNumber"
                    control={control}
                    rules={{
                      validate: (value) => validateRegistrationNumber(value, watchedLegalEntityType)
                    }}
                    render={({ field }) => (
                      <input
                        type="text"
                        className={`form-control ${companyErrors.companyRegNumber ? 'is-invalid' : ''}`}
                        id="companyRegNumber"
                        placeholder={registrationPlaceholderFor(watchedLegalEntityType)}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={async (e) => {
                          // Strip spaces, including around the slashes. "2021 / 359709 / 07"
                          // and "2021/359709/07" were stored as two different companies.
                          const trimmed = normalizeRegistrationNumber(e.target.value);
                          field.onChange(trimmed);
                          field.onBlur();
                          await trigger('company.companyRegNumber');
                        }}
                        aria-invalid={!!companyErrors.companyRegNumber}
                        aria-describedby={companyErrors.companyRegNumber ? 'companyRegNumber-error' : undefined}
                      />
                    )}
                  />
                  {companyErrors.companyRegNumber && (
                    <div id="companyRegNumber-error" className="invalid-feedback">
                      {companyErrors.companyRegNumber.message as string}
                    </div>
                  )}
                  {!companyErrors.companyRegNumber && selectedLegalEntityType?.usesOwnerIdentity && (
                    <small className="form-text text-muted">
                      This entity has no registration of its own, so it is identified by the
                      person behind it.
                    </small>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="registrationNumberType">Registration Number Type</label>
                  <select
                    className="form-select"
                    id="registrationNumberType"
                    {...register('company.registrationNumberType')}
                  >
                    <option value="">Not specified</option>
                    <option value={RegistrationNumberType.CIPC}>CIPC registration number</option>
                    <option value={RegistrationNumberType.PRACTICE_NUMBER}>Practice number</option>
                    <option value={RegistrationNumberType.TRUST_NUMBER}>Trust number (Master of the High Court)</option>
                    <option value={RegistrationNumberType.ID_NUMBER}>South African ID number</option>
                    <option value={RegistrationNumberType.OTHER}>Other</option>
                  </select>
                  <small className="form-text text-muted">
                    Defaults from the legal entity type. Change it if this business is known by a different number.
                  </small>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="taxNumber">Tax Number (Optional)</label>
                  <Controller
                    name="company.taxNumber"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!value || !value.trim()) return true;
                        return validateSouthAfricanTaxNumber(value);
                      }
                    }}
                    render={({ field }) => (
                      <input
                        type="text"
                        className={`form-control ${companyErrors.taxNumber ? 'is-invalid' : ''}`}
                        id="taxNumber"
                        placeholder="10-digit tax number"
                        value={field.value ?? ''}
                        inputMode="numeric"
                        maxLength={10}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={async (e) => {
                          const trimmed = normalizeTaxOrVatNumber(e.target.value);
                          field.onChange(trimmed);
                          field.onBlur();
                          await trigger('company.taxNumber');
                        }}
                        aria-invalid={!!companyErrors.taxNumber}
                        aria-describedby={companyErrors.taxNumber ? 'taxNumber-error' : undefined}
                      />
                    )}
                  />
                  {companyErrors.taxNumber && (
                    <div id="taxNumber-error" className="invalid-feedback">
                      {companyErrors.taxNumber.message as string || SOUTH_AFRICAN_TAX_NUMBER_ERROR}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="taxPin">SARS Tax PIN (Optional)</label>
                  <Controller
                    name="company.taxPin"
                    control={control}
                    rules={{
                      validate: (value) => !value || isValidSarsTaxPin(value) || SOUTH_AFRICAN_TAX_PIN_ERROR
                    }}
                    render={({ field }) => (
                      <input
                        type="text"
                        className={`form-control ${companyErrors.taxPin ? 'is-invalid' : ''}`}
                        id="taxPin"
                        placeholder="10-character SARS Tax PIN"
                        value={field.value ?? ''}
                        maxLength={10}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        onBlur={async (e) => {
                          field.onChange(e.target.value.trim().toUpperCase());
                          field.onBlur();
                          await trigger('company.taxPin');
                        }}
                        aria-invalid={!!companyErrors.taxPin}
                        aria-describedby={companyErrors.taxPin ? 'taxPin-error' : undefined}
                      />
                    )}
                  />
                  {companyErrors.taxPin && (
                    <div id="taxPin-error" className="invalid-feedback">
                      {companyErrors.taxPin.message as string || SOUTH_AFRICAN_TAX_PIN_ERROR}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="vatNumber">VAT Number (Optional)</label>
                  <Controller
                    name="company.vatNumber"
                    control={control}
                    rules={{
                      validate: validateSouthAfricanVatNumber
                    }}
                    render={({ field }) => (
                      <input
                        type="text"
                        className={`form-control ${companyErrors.vatNumber ? 'is-invalid' : ''}`}
                        id="vatNumber"
                        placeholder="10-digit VAT number (starts with 4)"
                        value={field.value ?? ''}
                        inputMode="numeric"
                        maxLength={10}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={async (e) => {
                          const trimmed = normalizeTaxOrVatNumber(e.target.value);
                          field.onChange(trimmed);
                          field.onBlur();
                          await trigger('company.vatNumber');
                        }}
                        aria-invalid={!!companyErrors.vatNumber}
                        aria-describedby={companyErrors.vatNumber ? 'vatNumber-error' : undefined}
                      />
                    )}
                  />
                  {companyErrors.vatNumber && (
                    <div id="vatNumber-error" className="invalid-feedback">
                      {companyErrors.vatNumber.message as string || SOUTH_AFRICAN_VAT_NUMBER_ERROR}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Person */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Contact Person / Company Representative</h6>
            <p className="text-muted small mb-3">
              The person Tirhelo deals with for this business. If it is one of the directors, choose which one.
              If it is someone else, capture their details and what gives them the authority to act for the business.
            </p>
            <div className="row g-3">
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="contactDirector">Is the contact person one of the directors?</label>
                  <select id="contactDirector" className="form-select" value={contactDirectorIndex}
                    onChange={e => chooseContactDirector(e.target.value)}>
                    <option value="">No, someone else</option>
                    {directors.map((_, i) => {
                      const name = directorName((watchedDirectors || [])[i]);
                      return <option key={i} value={String(i)}>Yes: Director {i + 1}{name ? ` (${name})` : ''}</option>;
                    })}
                  </select>
                </div>
              </div>
              {contactIsDirector ? (
                <div className="col-12">
                  <div className="alert alert-light border mb-0">
                    <i className="fa fa-user-check me-2 text-primary"></i>
                    The contact person is <strong>Director {Number(contactDirectorIndex) + 1}{directorName(contactDirector) ? `: ${directorName(contactDirector)}` : ''}</strong>.
                    {' '}Their name, ID number, cell number and email come from the Directors section below.
                    {(!contactDirector?.cellNumber || !contactDirector?.email) && (
                      <div className="text-danger small mt-1">
                        Capture Director {Number(contactDirectorIndex) + 1}&apos;s cell number and email below: they are this business&apos;s contact details.
                      </div>
                    )}
                  </div>
                </div>
              ) : (<>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="contactPersonFirstName">First Name</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.contactPersonFirstName ? 'is-invalid' : ''}`}
                    id="contactPersonFirstName"
                    placeholder="Enter first name"
                    {...register('company.contactPersonFirstName', {
                      required: 'First name is required'
                    })}
                  />
                  {companyErrors.contactPersonFirstName && (
                    <div className="invalid-feedback">{companyErrors.contactPersonFirstName.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="contactPersonLastName">Last Name</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.contactPersonLastName ? 'is-invalid' : ''}`}
                    id="contactPersonLastName"
                    placeholder="Enter last name"
                    {...register('company.contactPersonLastName', {
                      required: 'Last name is required'
                    })}
                  />
                  {companyErrors.contactPersonLastName && (
                    <div className="invalid-feedback">{companyErrors.contactPersonLastName.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="contactTel">Contact Number</label>
                  <input
                    type="tel"
                    className={`form-control ${companyErrors.contactTel ? 'is-invalid' : ''}`}
                    id="contactTel"
                    placeholder="0XXXXXXXXX"
                    inputMode="numeric"
                    maxLength={10}
                    {...register('company.contactTel', {
                      required: 'Contact number is required',
                      pattern: {
                        value: /^0\d{9}$/,
                        message: 'Please enter a valid South African 10-digit phone number'
                      }
                    })}
                  />
                  {companyErrors.contactTel && (
                    <div className="invalid-feedback">{companyErrors.contactTel.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="contactEmail">Email Address</label>
                  <input
                    type="email"
                    className={`form-control ${companyErrors.contactEmail ? 'is-invalid' : ''}`}
                    id="contactEmail"
                    placeholder="Enter email address"
                    {...register('company.contactEmail', {
                      required: 'Email address is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                  />
                  {companyErrors.contactEmail && (
                    <div className="invalid-feedback">{companyErrors.contactEmail.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="representativeCapacity">Capacity</label>
                  <select
                    className="form-control"
                    id="representativeCapacity"
                    {...register('company.representativeCapacity')}
                  >
                    <option value="">Select capacity</option>
                    <option value={RepresentativeCapacity.DIRECTOR}>Director</option>
                    <option value={RepresentativeCapacity.MEMBER}>Member</option>
                    <option value={RepresentativeCapacity.TRUSTEE}>Trustee</option>
                    <option value={RepresentativeCapacity.PARTNER}>Partner</option>
                    <option value={RepresentativeCapacity.EMPLOYEE}>Employee</option>
                    <option value={RepresentativeCapacity.ATTORNEY}>Attorney</option>
                    <option value={RepresentativeCapacity.AGENT}>Agent</option>
                    <option value={RepresentativeCapacity.OTHER}>Other</option>
                  </select>
                  <small className="form-text text-muted">
                    In what capacity they act for the business.
                  </small>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="representativeIdNumber">ID Number</label>
                  <input
                    type="text"
                    className="form-control"
                    id="representativeIdNumber"
                    placeholder="13-digit South African ID number"
                    maxLength={13}
                    {...register('company.representativeIdNumber')}
                  />
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="representativeMandate">Mandate</label>
                  <input
                    type="text"
                    className="form-control"
                    id="representativeMandate"
                    placeholder="e.g. board resolution of 12 May 2026"
                    {...register('company.representativeMandate')}
                  />
                  <small className="form-text text-muted">
                    What authorises them to act. Upload the signed mandate letter on the Upload Documents step.
                  </small>
                </div>
              </div>
              </>)}
            </div>
          </div>
        </div>

        {/* Company Address */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Company Address</h6>
            <div className="row g-3">
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="lineOne">Street Address</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.address?.lineOne ? 'is-invalid' : ''}`}
                    id="lineOne"
                    placeholder="Enter street address"
                    {...register('company.address.lineOne', {
                      required: 'Street address is required'
                    })}
                  />
                  {companyErrors.address?.lineOne && (
                    <div className="invalid-feedback">{companyErrors.address.lineOne.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="lineTwo">Building/Suite/Unit (Optional)</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.address?.lineTwo ? 'is-invalid' : ''}`}
                    id="lineTwo"
                    placeholder="Enter building, suite, or unit number"
                    {...register('company.address.lineTwo')}
                  />
                  {companyErrors.address?.lineTwo && (
                    <div className="invalid-feedback">{companyErrors.address.lineTwo.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="suburb">Suburb (Optional)</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.address?.suburb ? 'is-invalid' : ''}`}
                    id="suburb"
                    placeholder="Enter suburb"
                    {...register('company.address.suburb')}
                  />
                  {companyErrors.address?.suburb && (
                    <div className="invalid-feedback">{companyErrors.address.suburb.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.address?.city ? 'is-invalid' : ''}`}
                    id="city"
                    placeholder="Enter city"
                    {...register('company.address.city', {
                      required: 'City is required'
                    })}
                  />
                  {companyErrors.address?.city && (
                    <div className="invalid-feedback">{companyErrors.address.city.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="stateProvince">Province</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.address?.province ? 'is-invalid' : ''}`}
                    id="stateProvince"
                    placeholder="Enter province"
                    {...register('company.address.province', {
                      required: 'Province is required'
                    })}
                  />
                  {companyErrors.address?.province && (
                    <div className="invalid-feedback">{companyErrors.address.province.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label htmlFor="code">Postal Code</label>
                  <input
                    type="text"
                    className={`form-control ${companyErrors.address?.code ? 'is-invalid' : ''}`}
                    id="code"
                    placeholder="Enter postal code"
                    {...register('company.address.code', {
                      required: 'Postal code is required'
                    })}
                  />
                  {companyErrors.address?.code && (
                    <div className="invalid-feedback">{companyErrors.address.code.message as string}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Additional Information</h6>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="turnover">Annual Turnover (R)</label>
                  <input
                    type="number"
                    className={`form-control ${companyErrors.turnover ? 'is-invalid' : ''}`}
                    id="turnover"
                    placeholder="Enter annual turnover"
                    {...register('company.turnover', {
                      required: 'Annual turnover is required',
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: 'Turnover must be a positive number'
                      }
                    })}
                  />
                  {companyErrors.turnover && (
                    <div className="invalid-feedback">{companyErrors.turnover.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="industrySector">Industry Sector</label>
                 <select className={`form-select ${companyErrors.industrySector ? 'is-invalid' : ''}`}
                    id="industrySector"
                    {...register('company.industrySector')}
                  >
                    <option value="">Select industry sector</option>
                    <option value="TECHNOLOGY">Technology</option>
                    <option value="FINANCE">Financial Services</option>
                    <option value="HEALTHCARE">Healthcare</option>
                    <option value="CONSTRUCTION">Construction</option>
                    <option value="MANUFACTURING">Manufacturing</option>
                    <option value="RETAIL">Retail</option>
                    <option value="ENERGY">Energy</option>
                    <option value="AGRICULTURE">Agriculture</option>
                    <option value="LOGISTICS">Logistics and Transportation</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {companyErrors.industrySector && (
                    <div className="invalid-feedback">{companyErrors.industrySector.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="incomeType">Source Of Income</label>
                  <select className={`form-select ${companyErrors.incomeType ? 'is-invalid' : ''}`}
                    id="incomeType"
                    {...register('company.incomeType', { required: 'Source of income is required' })}
                  >
                    <option value="">Select source of income</option>
                    <option value="PO">Purchase Orders (PO)</option>
                    <option value="TENDER">Tender</option>
                    <option value="PRODUCTS_AND_SERVICES">Products and Services</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {companyErrors.incomeType && (
                    <div className="invalid-feedback">{companyErrors.incomeType.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor="industryCategory">Industry Category</label>
                  <select className={`form-select ${companyErrors.industryCategory ? 'is-invalid' : ''}`}
                    id="industryCategory"
                    {...register('company.industryCategory')}
                  >
                    <option value="">Select industry category</option>
                    <option value="GOVERNMENT">Government</option>
                    <option value="PRIVATE_SECTOR">Private Sector</option>
                    <option value="HOUSEHOLD">Household</option>
                    <option value="PERSONAL">Personal</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {companyErrors.industryCategory && (
                    <div className="invalid-feedback">{companyErrors.industryCategory.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="incomeDescription">Income Description</label>
                  <textarea
                    className={`form-control ${companyErrors.incomeDescription ? 'is-invalid' : ''}`}
                    id="incomeDescription"
                    placeholder="Describe the income source"
                    rows={3}
                    {...register('company.incomeDescription')}
                  />
                  {companyErrors.incomeDescription && (
                    <div className="invalid-feedback">{companyErrors.incomeDescription.message as string}</div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="industryDescription">Industry Description</label>
                  <textarea
                    className={`form-control ${companyErrors.industryDescription ? 'is-invalid' : ''}`}
                    id="industryDescription"
                    placeholder="Describe the industry category"
                    rows={3}
                    {...register('company.industryDescription')}
                  />
                  {companyErrors.industryDescription && (
                    <div className="invalid-feedback">{companyErrors.industryDescription.message as string}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Directors */}
        <div className="col-12">
          <div className="form-section">
            <h6 className="form-section-title">Company Directors</h6>

            {directors.map((director, index) => {
              const directorErrors = companyErrors.directors?.[index] || {};
              const currentIdType = watchedDirectors[index]?.idType;
              const currentIdNumber = watchedDirectors[index]?.idNumber;
              const isSAIDPopulated = currentIdType === 'SA_ID' && currentIdNumber && currentIdNumber.length === 13;

              return (
                <div key={director.id} className="row g-3 mb-4 pb-4 border-bottom">
                  {/* Director Header/Remove Button */}
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Director {index + 1}</h6>
                      {directors.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeDirector(index)}
                        >
                          <i className="fa fa-trash me-1"></i>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-firstName`}>First Name</label>
                      <input type="text" className={`form-control ${directorErrors.firstName ? 'is-invalid' : ''}`}
                        id={`director-${index}-firstName`} placeholder="Enter Directors first name"
                        {...register(`company.directors.${index}.firstName` as const, { required: 'First name is required' })}
                      />
                      {directorErrors.firstName && (<div className="invalid-feedback">{directorErrors.firstName.message as string}</div>)}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-lastName`}>Last Name</label>
                      <input type="text" className={`form-control ${directorErrors.lastName ? 'is-invalid' : ''}`}
                        id={`director-${index}-lastName`} placeholder="Enter Directors last name"
                        {...register(`company.directors.${index}.lastName` as const, { required: 'Last name is required' })}
                      />
                      {directorErrors.lastName && (<div className="invalid-feedback">{directorErrors.lastName.message as string}</div>)}
                    </div>
                  </div>
                  
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-idType`}>ID Type *</label>
                      <Controller
                        name={`company.directors.${index}.idType` as const}
                        control={control}
                        rules={{ required: 'ID Type is required' }}
                        render={({ field }) => (
                          <select
                            className={`form-select ${directorErrors.idType ? 'is-invalid' : ''}`}
                            id={`director-${index}-idType`}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const selectedIdType = (e.target.value || undefined) as 'SA_ID' | 'PASSPORT' | undefined;
                              field.onChange(selectedIdType);
                              updateDirectorIdLiveError(index, selectedIdType, watchedDirectors[index]?.idNumber, false);
                              void trigger(`company.directors.${index}.idNumber` as const);
                            }}
                            onBlur={field.onBlur}
                          >
                            <option value="">Select ID Type</option>
                            <option value="SA_ID">SA ID</option>
                            <option value="PASSPORT">Passport</option>
                          </select>
                        )}
                      />
                      {directorErrors.idType && (<div className="invalid-feedback">{directorErrors.idType.message as string}</div>)}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-id`}>ID Number *</label>
                      <Controller
                        name={`company.directors.${index}.idNumber` as const}
                        control={control}
                        rules={{
                          validate: (value) => {
                            const trimmed = normalizeTaxPinOrIdNumber(value);
                            if (!trimmed) {
                              return 'ID number is required';
                            }

                            if (watchedDirectors[index]?.idType === 'SA_ID') {
                              return validateSouthAfricanIdNumber(trimmed);
                            }

                            if (watchedDirectors[index]?.idType === 'PASSPORT') {
                              return validatePassportNumber(trimmed);
                            }

                            return true;
                          }
                        }}
                        render={({ field }) => {
                          const normalizedFieldValue = normalizeTaxPinOrIdNumber(field.value);
                          const immediateIdValidation = watchedDirectors[index]?.idType === 'SA_ID'
                            ? validateSouthAfricanIdNumber(normalizedFieldValue)
                            : watchedDirectors[index]?.idType === 'PASSPORT'
                              ? validatePassportNumber(normalizedFieldValue)
                              : true;
                          const immediateIdError =
                            (watchedDirectors[index]?.idType === 'SA_ID' || watchedDirectors[index]?.idType === 'PASSPORT') &&
                            normalizedFieldValue.length > 0 &&
                            immediateIdValidation !== true
                              ? immediateIdValidation
                              : '';

                          return (
                            <>
                              <input
                                type="text"
                                className={`form-control ${(directorIdLiveErrors[index] || immediateIdError || directorErrors.idNumber) ? 'is-invalid' : ''}`}
                                id={`director-${index}-id`}
                                placeholder={watchedDirectors[index]?.idType === 'SA_ID' ? '13-digit South African ID number' : 'Enter Directors ID Number'}
                                value={field.value ?? ''}
                                inputMode={watchedDirectors[index]?.idType === 'SA_ID' ? 'numeric' : undefined}
                                maxLength={watchedDirectors[index]?.idType === 'SA_ID' ? 13 : undefined}
                                onChange={(e) => {
                                  const nextValue = watchedDirectors[index]?.idType === 'SA_ID'
                                    ? e.target.value.slice(0, 13)
                                    : e.target.value;
                                  field.onChange(nextValue);
                                  updateDirectorIdLiveError(index, watchedDirectors[index]?.idType, nextValue, false);
                                  void trigger(`company.directors.${index}.idNumber` as const);
                                }}
                                onBlur={async (e) => {
                                  const fieldName = `company.directors.${index}.idNumber` as const;
                                  const trimmed = normalizeTaxPinOrIdNumber(e.target.value);
                                  field.onChange(trimmed);
                                  updateDirectorIdLiveError(index, watchedDirectors[index]?.idType, trimmed, true);
                                  field.onBlur();
                                  await trigger(fieldName);
                                }}
                              />
                              {(directorIdLiveErrors[index] || immediateIdError || directorErrors.idNumber) && (
                                <div className="invalid-feedback d-block">
                                  {directorIdLiveErrors[index] || immediateIdError || (directorErrors.idNumber?.message as string)}
                                </div>
                              )}
                            </>
                          );
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-dateOfBirth`}>Date of Birth</label>
                      <input type="date" className={`form-control ${directorErrors.dateOfBirth ? 'is-invalid' : ''}`}
                        id={`director-${index}-dateOfBirth`}
                        {...register(`company.directors.${index}.dateOfBirth` as const, {
                          required: 'Date of birth is required'
                        })}
                      />
                      {directorErrors.dateOfBirth && (<div className="invalid-feedback">{directorErrors.dateOfBirth.message as string}</div>)}
                    </div>
                  </div>
                  
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-email`}>Email Address</label>
                      <input type="email" className={`form-control ${directorErrors.email ? 'is-invalid' : ''}`}
                        id={`director-${index}-email`} placeholder="director@example.co.za" autoComplete="off"
                        {...register(`company.directors.${index}.email` as const)}
                      />
                      {directorErrors.email
                        ? (<div className="invalid-feedback">{directorErrors.email.message as string}</div>)
                        : (<small className="form-text text-muted">The director's own email, for messages to them such as birthday greetings.</small>)}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-cellNumber`}>Cell Number</label>
                      <input type="tel" inputMode="numeric" maxLength={10} className={`form-control ${directorErrors.cellNumber ? 'is-invalid' : ''}`}
                        id={`director-${index}-cellNumber`} placeholder="0821234567" autoComplete="off"
                        {...register(`company.directors.${index}.cellNumber` as const)}
                      />
                      {directorErrors.cellNumber
                        ? (<div className="invalid-feedback">{directorErrors.cellNumber.message as string}</div>)
                        : (<small className="form-text text-muted">Their own number, not the company's.</small>)}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-gender`}>Gender</label>
                      <select className={`form-select ${directorErrors.gender ? 'is-invalid' : ''}`}
                        id={`director-${index}-gender`}
                        {...register(`company.directors.${index}.gender` as const, { required: 'Gender is required' })}
                      >
                        <option value="">Select Gender</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="OTHER">Other</option>
                        <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                      </select>
                      {directorErrors.gender && (<div className="invalid-feedback">{directorErrors.gender.message as string}</div>)}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-nationality`}>Nationality</label>
                      <select className={`form-select ${directorErrors.nationality ? 'is-invalid' : ''}`}
                        id={`director-${index}-nationality`}
                        {...register(`company.directors.${index}.nationality` as const, { required: 'Nationality is required' })}
                      >
                        <option value="">Select Nationality</option>
                        <option value="SOUTH_AFRICA">South Africa</option>
                        <option value="NAMIBIA">Namibia</option>
                        <option value="ZIMBABWE">Zimbabwe</option>
                        <option value="UNITED_KINGDOM">United Kingdom</option>
                        <option value="OTHER">Other</option>
                      </select>
                      {directorErrors.nationality && (<div className="invalid-feedback">{directorErrors.nationality.message as string}</div>)}
                    </div>
                  </div>
                  
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-race`}>Race</label>
                      <select className={`form-select ${directorErrors.race ? 'is-invalid' : ''}`}
                        id={`director-${index}-race`}
                        {...register(`company.directors.${index}.race` as const, { required: 'Race is required' })}
                      >
                        <option value="">Select Race</option>
                        <option value="BLACK">Black</option>
                        <option value="COLOURED">Coloured</option>
                        <option value="WHITE">White</option>
                        <option value="ASIAN">Asian</option>
                        <option value="OTHER">Other</option>
                        <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                      </select>
                      {directorErrors.race && (<div className="invalid-feedback">{directorErrors.race.message as string}</div>)}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-designation`}>Designation</label>
                      <input type="text" className={`form-control ${directorErrors.designation ? 'is-invalid' : ''}`}
                        id={`director-${index}-designation`} placeholder="Enter designation"
                        {...register(`company.directors.${index}.designation` as const, { required: 'Designation is required' })}
                      />
                      {directorErrors.designation && (<div className="invalid-feedback">{directorErrors.designation.message as string}</div>)}
                    </div>
                  </div>
                  
                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-ownershipPercentage`}>Ownership Percentage (%)</label>
                      <input type="number" className={`form-control ${directorErrors.ownershipPercentage ? 'is-invalid' : ''}`}
                        id={`director-${index}-ownershipPercentage`} placeholder="0" min="0" max="100" step="0.01"
                        {...register(`company.directors.${index}.ownershipPercentage` as const, {
                          valueAsNumber: true,
                          min: { value: 0, message: 'Must be between 0 and 100' },
                          max: { value: 100, message: 'Must be between 0 and 100' },
                        })}
                      />
                      {directorErrors.ownershipPercentage && (<div className="invalid-feedback">{directorErrors.ownershipPercentage.message as string}</div>)}
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="form-group">
                      <label htmlFor={`director-${index}-appointmentDate`}>Appointment Date</label>
                      <input type="date" className={`form-control ${directorErrors.appointmentDate ? 'is-invalid' : ''}`}
                        id={`director-${index}-appointmentDate`}
                        max={todayDate}
                        {...register(`company.directors.${index}.appointmentDate` as const, {
                          validate: (value) => {
                            if (!value) return true;
                            return value <= todayDate || 'Appointment date cannot be in the future';
                          }
                        })}
                      />
                      {directorErrors.appointmentDate && (<div className="invalid-feedback">{directorErrors.appointmentDate.message as string}</div>)}
                    </div>
                  </div>
                  
           <div className="col-12">
                    <div className="d-flex flex-column">
                        <div className="form-check form-check-inline mb-2">
                          <input type="checkbox" className="form-check-input"
                            id={`director-${index}-isDisabled`}
                            {...register(`company.directors.${index}.isDisabled` as const)}
                          />
                          <label className="form-check-label" htmlFor={`director-${index}-isDisabled`}>Person with disability</label>
                        </div>

                        <div className="form-check form-check-inline mb-2">
                          <input type="checkbox" className="form-check-input"
                            id={`director-${index}-isPreviouslyDisadvantaged`}
                            {...register(`company.directors.${index}.isPreviouslyDisadvantaged` as const)}
                          />
                          <label className="form-check-label" htmlFor={`director-${index}-isPreviouslyDisadvantaged`}>Previously disadvantaged individual</label>
                        </div>
                    </div>
                  </div>

                </div>
              );
            })}

            <div className="col-12 mt-3">
              <button type="button" className="btn btn-outline-primary" onClick={addDirector}>
                <i className="fa fa-plus me-2"></i>
                Add Another Director
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
